import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction } from '@yuan-math/math';
import {
  choiceExercise,
  expr,
  fractionExercise,
  fractionTrap,
  leadingTerm,
  numericExercise,
  options,
  signedConstant,
  signedTerm,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Sequences, series, the convergence tests, and power series.
 *
 * Almost every item in this file asks for a *verdict* rather than a number, and
 * that is the point rather than a limitation. The series unit is where a student
 * first has to produce an argument, and a bank that only ever asked for sums
 * would test the two families whose sums can be found — geometric and
 * telescoping — and nothing else in the unit.
 *
 * So the multiple-choice items here carry the weight, and their distractors come
 * from the small set of beliefs that actually cause failure:
 *
 *  - terms tending to zero read as convergence,
 *  - a ratio limit of 1 read as a verdict rather than as silence,
 *  - the p-series boundary placed at p ≥ 1,
 *  - a comparison made in the direction that concludes nothing,
 *  - the alternating series test taken to establish absolute convergence.
 *
 * Each of those is a coherent position rather than a slip, which is why each is
 * a named misconception attached to a distractor rather than a wrong number.
 *
 * The pins worth knowing about:
 *
 * `geometric-sum` sums from n = 1 rather than n = 0. Starting at zero makes the
 * first term a, the formula a/(1 − r) then applies with no thought, and the item
 * stops testing the thing students get wrong — which is identifying a at all.
 *
 * `ratio-test-limit` keeps the two bases distinct, because at c = d the limit is
 * 1, the answer is the inconclusive case, and asking for it as a number rewards
 * a student who has not noticed that the test told them nothing.
 *
 * `alternating-terms-needed` answers with n rather than n + 1. The error bound
 * names the first term *left out*, so the count of terms *used* is one less, and
 * that off-by-one is the entire content of the item.
 *
 * `comparison-series-choice` keeps the numerator degree at 1 or more. At degree
 * zero the right comparison is 1/n^k, which is also one of the distractors, and
 * an item with two identical options is not an item.
 */

/** A number for prose rather than for LaTeX — a real minus sign, not a hyphen. */
const minus = (value: number | string): string => String(value).replace(/-/g, '−');

/** A ratio as LaTeX, with the sign kept outside the fraction. */
const ratioLatex = (p: number, q: number): string =>
  p < 0 ? `-\\frac{${-p}}{${q}}` : `\\frac{${p}}{${q}}`;

/** `x`, `x - 3`, `x + 3` — the shift of a power series, never `x ` with a gap. */
const shifted = (centre: number): string => (centre === 0 ? 'x' : `x ${signedConstant(-centre)}`);

/** A quadratic in n, with unit coefficients and vanished terms handled. */
const quadraticInN = (a: number, b: number, c: number): string =>
  expr(leadingTerm(a, 'n^{2}'), signedTerm(b, 'n'), signedConstant(c));

/** `n^{p}`, except that nobody writes `n^{1}`. */
const powerOfN = (printed: string): string => (printed === '1' ? 'n' : `n^{${printed}}`);

export const SERIES_TEMPLATES: ExerciseTemplate[] = [
  /* --------------------------------------------------------- sequences */
  template({
    id: 'sequence-rational-limit',
    conceptId: 'sequence-limits',
    description: 'The limit of a ratio of quadratics in n.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.int(1, 9);
      const q = rng.int(-6, 6);
      const r = rng.int(-8, 8);
      const s = rng.int(1, 9);
      const t = rng.int(-6, 6);
      const u = rng.int(-8, 8);
      const answer = fraction(p, s);
      const firstDenominator = s + t + u;
      const firstTerm = firstDenominator === 0 ? null : fraction(p + q + r, firstDenominator);

      // The opening term is only a trap when it differs from the limit; often
      // enough it does not, and a trap sitting on the answer diagnoses nothing.
      const traps: Trap[] = [];
      if (firstTerm && !(firstTerm.n === answer.n && firstTerm.d === answer.d)) {
        traps.push(fractionTrap(firstTerm, 'sequence-judged-from-early-terms'));
      }

      return fractionExercise({
        prompt: 'Find the limit of this sequence. Give your answer as a fraction.',
        latex: `a_{n} = \\frac{${quadraticInN(p, q, r)}}{${quadraticInN(s, t, u)}}`,
        value: answer,
        summary: `Divide top and bottom by n². Every term with an n underneath tends to 0, leaving the ratio of the leading coefficients: ${p}/${s} = ${answer.n}/${answer.d}.`,
        hint: 'Divide through by the highest power of n that appears anywhere.',
        steps: [
          { label: 'Divide top and bottom by n².', latex: `a_{n} = \\frac{${p} + \\frac{${q}}{n} + \\frac{${r}}{n^{2}}}{${s} + \\frac{${t}}{n} + \\frac{${u}}{n^{2}}}` },
          { label: 'Each term with an n underneath vanishes.', latex: `\\lim_{n\\to\\infty} a_{n} = \\frac{${p}}{${s}}` },
          { label: 'In lowest terms.', latex: `\\frac{${answer.n}}{${answer.d}}` },
        ],
        traps,
      });
    },
  }),

  template({
    id: 'sequence-growth-order',
    conceptId: 'sequence-limits',
    description: 'Order two candidates from the growth hierarchy.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const power = rng.pick([5, 10, 20, 50, 100]);
      const base = rng.pick([2, 3, 5]);
      const fastest = rng.bool();

      return choiceExercise({
        prompt: fastest
          ? 'Which of these grows fastest as n becomes large?'
          : 'Which of these grows slowest as n becomes large?',
        ...options(
          [
            { latex: 'n!', correct: fastest, ...(fastest ? {} : { misconceptionId: 'sequence-growth-order-confused' }) },
            { latex: `${base}^{n}`, misconceptionId: 'sequence-growth-order-confused' },
            { latex: `n^{${power}}`, misconceptionId: 'sequence-growth-order-confused' },
            { latex: '\\ln n', correct: !fastest, ...(fastest ? { misconceptionId: 'sequence-growth-order-confused' } : {}) },
          ],
          rng.shuffle,
        ),
        summary: `The hierarchy runs ln n, then any power of n, then any exponential, then n!, then n to the n — and each beats everything before it however large the exponent or the base. So n! outgrows ${base} to the n, which outgrows n to the ${power}, however unlikely that looks for the first few hundred terms.`,
        hint: 'The exponent on n and the base of the exponential are both irrelevant. Only the *kind* of growth matters.',
      });
    },
  }),

  template({
    id: 'sequence-geometric-limit',
    conceptId: 'sequence-limits',
    description: 'Decide whether a geometric sequence settles.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.int(1, 9);
      let q = rng.int(2, 9);
      if (q === p) q = p + 1;
      const negative = rng.bool();
      const numerator = negative ? -p : p;
      const converges = p < q;

      return choiceExercise({
        prompt: 'What is the limit of this sequence?',
        latex: `a_{n} = \\left(${ratioLatex(numerator, q)}\\right)^{n}`,
        ...options(
          [
            { label: '0', correct: converges, ...(converges ? {} : { misconceptionId: 'sequence-growth-order-confused' }) },
            { label: 'It has no limit — the sequence diverges', correct: !converges, ...(converges ? { misconceptionId: 'sequence-judged-from-early-terms' } : {}) },
            { latex: ratioLatex(numerator, q), misconceptionId: 'sequence-judged-from-early-terms' },
            { label: 'It settles at 1', misconceptionId: 'sequence-growth-order-confused' },
          ],
          rng.shuffle,
        ),
        summary: converges
          ? `The ratio has size ${p}/${q}, which is below 1, so repeated multiplication drags the terms to 0${negative ? ' — alternating in sign on the way, which does not stop them converging' : ''}.`
          : `The ratio has size ${p}/${q}, which is above 1, so the terms grow without bound${negative ? ' while flipping sign' : ''} and the sequence has no limit.`,
        hint: 'rⁿ tends to 0 exactly when |r| < 1. Compare the size of the ratio with 1, ignoring its sign.',
      });
    },
  }),

  /* ------------------------------------------------------------ series */
  template({
    id: 'geometric-sum',
    conceptId: 'series-convergence',
    description: 'Sum an infinite geometric series that does not start at n = 0.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(1, 9);
      const q = rng.int(2, 7);
      let p = rng.int(1, q - 1);
      if (rng.bool()) p = -p;
      const answer = fraction(a * p, q - p);
      const wrongFirstTerm = fraction(a * q, q - p);

      return fractionExercise({
        prompt: 'Find the sum of this series. Give your answer as a fraction.',
        latex: `\\sum_{n=1}^{\\infty} ${a === 1 ? '' : a}\\left(${ratioLatex(p, q)}\\right)^{n}`,
        value: answer,
        summary: `The ratio is ${minus(p)}/${q}, whose size is below 1, so the series converges. It starts at n = 1, so the first term is ${minus(a * p)}/${q} rather than ${a}. Then a/(1 − r) gives ${answer.n}/${answer.d}.`,
        hint: 'a is the first term the series actually produces. Put n = 1 in and see what comes out.',
        steps: [
          { label: 'Identify the ratio and check that it converges.', latex: `r = ${ratioLatex(p, q)}, \\qquad |r| < 1` },
          { label: 'Identify the first term, at n = 1.', latex: `a = ${ratioLatex(a * p, q)}` },
          { label: 'Apply the formula.', latex: `S = \\frac{a}{1 - r} = \\frac{${ratioLatex(a * p, q)}}{\\frac{${q - p}}{${q}}}` },
          { label: 'Simplify.', latex: `S = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(wrongFirstTerm, 'geometric-first-term-misidentified')],
      });
    },
  }),

  template({
    id: 'divergence-test-verdict',
    conceptId: 'series-convergence',
    description: 'Apply the test for divergence, including where it decides nothing.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = rng.int(1, 9);
      const s = rng.int(1, 9);
      // Positive throughout, so no term of either series is ever undefined.
      const t = rng.int(1, 6);
      const shrinking = rng.bool();
      const latex = shrinking
        ? `\\sum_{n=1}^{\\infty} \\frac{${leadingTerm(p, 'n')}}{${quadraticInN(s, t, 0)}}`
        : `\\sum_{n=1}^{\\infty} \\frac{${leadingTerm(p, 'n')}}{${expr(leadingTerm(s, 'n'), signedConstant(t))}}`;

      return choiceExercise({
        prompt: 'What does the test for divergence tell you about this series?',
        latex,
        ...options(
          [
            {
              label: 'Nothing — the terms tend to zero, so the test gives no information',
              correct: shrinking,
              ...(shrinking ? {} : { misconceptionId: 'terms-to-zero-taken-as-convergence' }),
            },
            {
              label: 'It diverges, because the terms do not tend to zero',
              correct: !shrinking,
              ...(shrinking ? { misconceptionId: 'terms-to-zero-taken-as-convergence' } : {}),
            },
            { label: 'It converges, because the terms tend to zero', misconceptionId: 'terms-to-zero-taken-as-convergence' },
            { label: 'It converges, because the terms are eventually decreasing', misconceptionId: 'alternating-decreasing-condition-skipped' },
          ],
          rng.shuffle,
        ),
        summary: shrinking
          ? `The terms behave like ${p}/(${s}n), which tends to 0 — so the test for divergence is silent. That is not convergence: the harmonic series has terms tending to zero and grows without bound.`
          : `The terms tend to ${p}/${s}, which is not zero, so the partial sums increase by roughly that much for ever and the series diverges.`,
        hint: 'Take the limit of the terms first. Only a non-zero limit lets this test conclude anything.',
      });
    },
  }),

  template({
    id: 'geometric-convergence-check',
    conceptId: 'series-convergence',
    description: 'Decide whether a geometric series converges before applying the formula to it.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(1, 9);
      const q = rng.int(2, 7);
      // p = q would divide by zero in the formula, which is a different lesson.
      let p = rng.int(1, q + 3);
      if (p === q) p = q + 1;
      if (rng.bool()) p = -p;
      const converges = Math.abs(p) < q;
      const formulaValue = fraction(a * q, q - p);
      const secondTermValue = fraction(a * p, q - p);

      return choiceExercise({
        prompt: 'Does this series converge, and if so what is its sum?',
        latex: `\\sum_{n=0}^{\\infty} ${a === 1 ? '' : a}\\left(${ratioLatex(p, q)}\\right)^{n}`,
        ...options(
          [
            {
              label: `It converges, to ${formulaValue.d === 1 ? `${formulaValue.n}` : `${formulaValue.n}/${formulaValue.d}`}`,
              correct: converges,
              ...(converges ? {} : { misconceptionId: 'geometric-formula-used-outside-radius' }),
            },
            {
              label: 'It diverges — there is no sum',
              correct: !converges,
              ...(converges ? { misconceptionId: 'terms-to-zero-taken-as-convergence' } : {}),
            },
            {
              label: `It converges, to ${secondTermValue.d === 1 ? `${secondTermValue.n}` : `${secondTermValue.n}/${secondTermValue.d}`}`,
              misconceptionId: 'geometric-first-term-misidentified',
            },
            { label: 'It converges, because the terms are getting smaller', misconceptionId: 'terms-to-zero-taken-as-convergence' },
          ],
          rng.shuffle,
        ),
        summary: converges
          ? `The ratio is ${minus(p)}/${q}, whose size is below 1, so the series converges. Starting at n = 0 makes the first term ${a}, and a/(1 - r) gives ${formulaValue.n}${formulaValue.d === 1 ? '' : `/${formulaValue.d}`}.`
          : `The ratio has size ${Math.abs(p)}/${q}, which is not below 1, so the terms do not shrink and the partial sums run away. The formula still returns a number — ${formulaValue.n}${formulaValue.d === 1 ? '' : `/${formulaValue.d}`} — and that number is meaningless here, which is the clearest sign that the condition was doing real work.`,
        hint: 'Check the size of the ratio against 1 before reaching for a/(1 - r).',
      });
    },
  }),

  template({
    id: 'telescoping-partial-sum',
    conceptId: 'series-convergence',
    description: 'Collapse a telescoping partial sum to its two surviving ends.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const N = rng.int(2, 20);
      const answer = fraction(N, N + 1);
      const lastTermOnly = fraction(1, N + 1);

      return fractionExercise({
        prompt: 'Evaluate this partial sum. Give your answer as a fraction.',
        latex: `\\sum_{n=1}^{${N}} \\left(\\frac{1}{n} - \\frac{1}{n+1}\\right)`,
        value: answer,
        summary: `Every interior piece appears twice with opposite signs and cancels, leaving the first term of the first bracket and the second term of the last: 1 − 1/${N + 1} = ${answer.n}/${answer.d}.`,
        hint: 'Write out three or four brackets in a row and see what survives.',
        steps: [
          { label: 'Write the first brackets out.', latex: `\\left(1 - \\tfrac{1}{2}\\right) + \\left(\\tfrac{1}{2} - \\tfrac{1}{3}\\right) + \\cdots + \\left(\\tfrac{1}{${N}} - \\tfrac{1}{${N + 1}}\\right)` },
          { label: 'Everything in the middle cancels.', latex: `s_{${N}} = 1 - \\frac{1}{${N + 1}}` },
          { label: 'Combine.', latex: `s_{${N}} = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(lastTermOnly, 'telescoping-ends-mismatched')],
      });
    },
  }),

  /* ------------------------------------------------------ integral test */
  template({
    id: 'p-series-verdict',
    conceptId: 'integral-test',
    description: 'Classify a p-series, including at the boundary.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const p = fraction(rng.int(1, 7), rng.pick([1, 2, 3]));
      const converges = p.n > p.d;
      const printed = p.d === 1 ? `${p.n}` : `${p.n}/${p.d}`;

      return choiceExercise({
        prompt: 'Does this series converge?',
        latex: `\\sum_{n=1}^{\\infty} \\frac{1}{${powerOfN(printed)}}`,
        ...options(
          [
            {
              label: `Yes — it is a p-series with p = ${printed}, and p is greater than 1`,
              correct: converges,
              ...(converges ? {} : { misconceptionId: 'p-series-threshold-misremembered' }),
            },
            {
              label: `No — it is a p-series with p = ${printed}, and p is not greater than 1`,
              correct: !converges,
              ...(converges ? { misconceptionId: 'p-series-threshold-misremembered' } : {}),
            },
            { label: 'Yes — the terms tend to zero', misconceptionId: 'terms-to-zero-taken-as-convergence' },
            { label: 'The ratio test gives 1, so it converges', misconceptionId: 'ratio-test-one-read-as-verdict' },
          ],
          rng.shuffle,
        ),
        summary: converges
          ? `p = ${printed} is strictly greater than 1, so the matching improper integral converges and the series does too.`
          : `p = ${printed} is not greater than 1, so the integral of x to the minus p diverges out at infinity and the series goes with it. At p = 1 exactly, this is the harmonic series.`,
        hint: 'The boundary is strict: p > 1 converges, and p = 1 is the harmonic series.',
      });
    },
  }),

  template({
    id: 'integral-test-bounds',
    conceptId: 'integral-test',
    description: 'Say what the integral test does and does not tell you about the sum.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = rng.int(1, 6);
      const p = rng.int(3, 8);
      const integral = `\\frac{${c}}{${p - 1}}`;

      return choiceExercise({
        prompt: `The integral of ${c}/x^${p} from 1 to infinity is ${c}/${p - 1}. What does the integral test let you conclude about the sum S?`,
        latex: `S = \\sum_{n=1}^{\\infty} \\frac{${c}}{n^{${p}}}`,
        ...options(
          [
            { latex: `${integral} \\leq S \\leq ${c} + ${integral}`, correct: true },
            { latex: `S = ${integral}`, misconceptionId: 'integral-value-taken-as-sum' },
            { latex: `S = ${c} + ${integral}`, misconceptionId: 'integral-value-taken-as-sum' },
            { label: 'Nothing at all — the test applies only to alternating series', misconceptionId: 'integral-test-conditions-unchecked' },
          ],
          rng.shuffle,
        ),
        summary: `The rectangles bound the curve from both sides, so the sum and the integral are within one term of each other: the sum is at least the integral and at most the first term plus the integral. The test decides convergence and never produces the total.`,
        hint: 'Draw the rectangles twice, once at the left endpoints and once at the right.',
      });
    },
  }),

  template({
    id: 'integral-test-applies',
    conceptId: 'integral-test',
    description: 'Check the three conditions before applying the integral test.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(2, 6);
      const c = rng.int(2, 9);

      return choiceExercise({
        prompt: 'The integral test needs terms that are positive, continuous and eventually decreasing. Which of these series does it apply to?',
        ...options(
          [
            { latex: `\\sum_{n=1}^{\\infty} \\frac{1}{n^{${p}} + ${c}}`, correct: true },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{(-1)^{n}}{n^{${p}}}`, misconceptionId: 'integral-test-conditions-unchecked' },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{n^{${p}}}{n^{${p}} + ${c}}`, misconceptionId: 'integral-test-conditions-unchecked' },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{\\sin n}{n^{${p}}}`, misconceptionId: 'integral-test-conditions-unchecked' },
          ],
          rng.shuffle,
        ),
        summary: `Only the first has terms that are positive and decreasing throughout. The alternating one and the one with sin n change sign, so the rectangles do not bound the curve; the third has terms climbing towards 1, which fails the test for divergence on its own.`,
        hint: 'Two of the wrong answers change sign. The third has terms that do not shrink at all.',
      });
    },
  }),

  /* ------------------------------------------------------- comparisons */
  template({
    id: 'comparison-series-choice',
    conceptId: 'comparison-tests',
    description: 'Choose the comparison series from the dominant behaviour.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // j ≥ 1 keeps the right comparison, 1/n^{k−j}, away from the distractor
      // 1/n^{k}; k − j ≥ 2 keeps it away from the harmonic distractor.
      const j = rng.int(1, 2);
      const k = rng.int(j + 2, j + 4);
      const alpha = rng.int(1, 9);
      const beta = rng.int(1, 9);
      const shift = rng.int(1, 9);
      const numerator = expr(leadingTerm(alpha, j === 1 ? 'n' : `n^{${j}}`), signedConstant(shift));
      const denominator = expr(leadingTerm(beta, `n^{${k}}`), signedConstant(shift));

      return choiceExercise({
        prompt: 'Which series is the natural one to compare this with?',
        latex: `\\sum_{n=1}^{\\infty} \\frac{${numerator}}{${denominator}}`,
        ...options(
          [
            { latex: `\\sum \\frac{1}{n^{${k - j}}}`, correct: true },
            { latex: `\\sum \\frac{1}{n^{${k}}}`, misconceptionId: 'comparison-series-misjudged' },
            { latex: `\\sum \\frac{1}{n^{${k + j}}}`, misconceptionId: 'comparison-series-misjudged' },
            { latex: '\\sum \\frac{1}{n}', misconceptionId: 'comparison-series-misjudged' },
          ],
          rng.shuffle,
        ),
        summary: `Keep the leading term on the top and the leading term underneath: ${alpha}n to the ${j} over ${beta}n to the ${k} behaves like a constant times 1 over n to the ${k - j}. The constant does not affect convergence, so it is dropped.`,
        hint: 'Degree on the top minus degree underneath. Everything else is noise for large n.',
      });
    },
  }),

  template({
    id: 'comparison-direction',
    conceptId: 'comparison-tests',
    description: 'Decide whether a comparison points in a direction that concludes.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(2, 5);
      const c = rng.int(1, 9);
      const bigger = rng.bool();
      // Adding to the denominator makes the terms smaller; subtracting makes
      // them bigger, and only one of the two directions concludes.
      const latex = bigger
        ? `\\sum_{n=${c + 1}}^{\\infty} \\frac{1}{n^{${p}} - ${c}}`
        : `\\sum_{n=1}^{\\infty} \\frac{1}{n^{${p}} + ${c}}`;

      return choiceExercise({
        prompt: `Compare this series with the convergent series Σ1/n^${p}. What follows by direct comparison?`,
        latex,
        ...options(
          [
            {
              label: 'It converges — its terms are smaller than those of a convergent series',
              correct: !bigger,
              ...(bigger ? { misconceptionId: 'comparison-inequality-reversed' } : {}),
            },
            {
              label: 'Nothing — its terms are larger, and that direction does not conclude',
              correct: bigger,
              ...(bigger ? {} : { misconceptionId: 'comparison-inequality-reversed' }),
            },
            { label: 'It diverges — its terms are larger than those of a convergent series', misconceptionId: 'comparison-inequality-reversed' },
            { label: 'It diverges — its terms are smaller than those of a convergent series', misconceptionId: 'comparison-inequality-reversed' },
          ],
          rng.shuffle,
        ),
        summary: bigger
          ? `Subtracting ${c} makes the denominator smaller and the terms *larger* than 1/n^${p}. Larger than convergent concludes nothing — switch to limit comparison, where the ratio tends to 1 and the verdict follows immediately.`
          : `Adding ${c} makes the denominator larger and the terms smaller than 1/n^${p}. Smaller than convergent is one of the two useful directions, so this series converges.`,
        hint: 'A bigger denominator makes a smaller fraction. Then ask whether that direction is one of the two that conclude.',
      });
    },
  }),

  template({
    id: 'limit-comparison-value',
    conceptId: 'comparison-tests',
    description: 'Compute the limit of the ratio in a limit comparison.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const j = rng.int(1, 2);
      const k = rng.int(j + 1, j + 3);
      const alpha = rng.int(2, 9);
      let beta = rng.int(2, 9);
      // α = β makes the limit 1, where the inverted trap is the answer.
      if (beta === alpha) beta = alpha === 9 ? 2 : alpha + 1;
      const shift = rng.int(1, 6);
      const answer = fraction(alpha, beta);
      const inverted = fraction(beta, alpha);
      const numerator = expr(leadingTerm(alpha, j === 1 ? 'n' : `n^{${j}}`), signedConstant(shift));
      const denominator = expr(leadingTerm(beta, `n^{${k}}`), signedConstant(shift));

      return fractionExercise({
        prompt: `Find the limit of aₙ/bₙ, where bₙ = 1/n^${k - j}. Give your answer as a fraction.`,
        latex: `a_{n} = \\frac{${numerator}}{${denominator}}`,
        value: answer,
        summary: `Multiplying by n to the ${k - j} leaves a ratio of two degree-${k} polynomials, whose limit is ${alpha}/${beta} = ${answer.n}/${answer.d}. That is finite and non-zero, so the two series behave alike.`,
        hint: `Multiply aₙ by n to the ${k - j} and take the limit of the ratio of polynomials.`,
        steps: [
          { label: 'Divide by bₙ — that is, multiply by that power of n.', latex: `\\frac{a_{n}}{b_{n}} = \\frac{n^{${k - j}}\\left(${numerator}\\right)}{${denominator}}` },
          { label: `Both top and bottom are now degree ${k}.`, latex: `= \\frac{${alpha}n^{${k}} + \\cdots}{${beta}n^{${k}} + \\cdots}` },
          { label: 'Take the limit.', latex: `L = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(inverted, 'comparison-series-misjudged')],
      });
    },
  }),

  /* -------------------------------------------------------- ratio test */
  template({
    id: 'ratio-test-limit',
    conceptId: 'ratio-test',
    description: 'Compute the ratio limit for a series with an exponential in it.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      // c < d always: at c = d the limit is 1, and the item would be asking for
      // the value of a test that has said nothing.
      const c = rng.int(2, 7);
      const d = rng.int(c + 1, 9);
      const k = rng.int(1, 3);
      const answer = fraction(c, d);
      const inverted = fraction(d, c);

      return fractionExercise({
        prompt: 'Find the ratio limit L for this series. Give your answer as a fraction.',
        latex: `\\sum_{n=1}^{\\infty} \\frac{n^{${k}}\\,${c}^{n}}{${d}^{n}}`,
        value: answer,
        summary: `The ratio of consecutive terms is ((n+1)/n) to the ${k}, times ${c}/${d}, and the bracket tends to 1. So L = ${answer.n}/${answer.d}, which is below 1 — the series converges absolutely.`,
        hint: 'Write aₙ₊₁/aₙ and cancel. The polynomial factor always tends to 1; only the exponential survives.',
        steps: [
          { label: 'Form the ratio.', latex: `\\frac{a_{n+1}}{a_{n}} = \\frac{(n+1)^{${k}}}{n^{${k}}}\\cdot\\frac{${c}}{${d}}` },
          { label: 'The polynomial factor tends to 1.', latex: `\\left(\\frac{n+1}{n}\\right)^{${k}} \\to 1` },
          { label: 'So the limit is the constant that is left.', latex: `L = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(inverted, 'ratio-test-inverted')],
      });
    },
  }),

  template({
    id: 'ratio-test-verdict',
    conceptId: 'ratio-test',
    description: 'Read the verdict off a ratio limit, including when there is none.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const kind = rng.pick(['below', 'above', 'one'] as const);
      const p = rng.int(1, 8);
      const q = rng.int(p + 1, 9);
      const shown = kind === 'one' ? '1' : kind === 'below' ? `${p}/${q}` : `${q}/${p}`;

      return choiceExercise({
        prompt: `The ratio test on a series gives L = ${shown}. What does that tell you?`,
        ...options(
          [
            {
              label: 'The series converges absolutely',
              correct: kind === 'below',
              ...(kind === 'below' ? {} : { misconceptionId: 'ratio-test-one-read-as-verdict' }),
            },
            {
              label: 'The series diverges',
              correct: kind === 'above',
              ...(kind === 'above' ? {} : { misconceptionId: 'ratio-test-one-read-as-verdict' }),
            },
            {
              label: 'Nothing — the test gives no information here',
              correct: kind === 'one',
              ...(kind === 'one' ? {} : { misconceptionId: 'ratio-test-inverted' }),
            },
            { label: 'The series converges conditionally', misconceptionId: 'conditional-taken-as-absolute' },
          ],
          rng.shuffle,
        ),
        summary:
          kind === 'one'
            ? 'A ratio limit of exactly 1 is the test returning nothing. Every p-series gives 1, and those include both the divergent harmonic series and the convergent sum of 1/n² — so the ratio cannot be deciding between them.'
            : kind === 'below'
              ? `L = ${shown} is below 1, so far enough out the series is dominated by a convergent geometric series. That gives absolute convergence, which is the strong kind.`
              : `L = ${shown} is above 1, so the terms eventually grow rather than shrink and cannot tend to zero. The series diverges.`,
        hint: 'Compare L with 1. Only three things can happen, and one of them is that nothing happens.',
      });
    },
  }),

  template({
    id: 'ratio-test-applies',
    conceptId: 'ratio-test',
    description: 'Choose the series the ratio test can actually decide.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = rng.int(2, 9);
      const p = rng.int(2, 6);
      const e = rng.int(2, 9);

      return choiceExercise({
        prompt: 'The ratio test decides some series and returns nothing on others. Which of these does it decide?',
        ...options(
          [
            { latex: `\\sum_{n=1}^{\\infty} \\frac{${c}^{n}}{n!}`, correct: true },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{1}{n^{${p}}}`, misconceptionId: 'ratio-test-one-read-as-verdict' },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{${e}}{n}`, misconceptionId: 'ratio-test-one-read-as-verdict' },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{n}{n^{${p}} + ${e}}`, misconceptionId: 'ratio-test-one-read-as-verdict' },
          ],
          rng.shuffle,
        ),
        summary: `Only the factorial gives a ratio limit away from 1: it is ${c}/(n + 1), which tends to 0. Every ratio of polynomials in n gives L = 1, so the ratio test is silent on all three of the others — and that is a fact about ratios rather than a failure of arithmetic.`,
        hint: 'Factorials and exponentials are what the ratio test is for. Powers of n are what it cannot see.',
      });
    },
  }),

  /* ------------------------------------------------- alternating series */
  template({
    id: 'alternating-terms-needed',
    conceptId: 'alternating-series',
    description: 'Use the alternating-series error bound to size a partial sum.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const power = rng.pick([1, 2]);
      const m = rng.int(2, 60) * 10;
      const needed = power === 1 ? m : Math.ceil(Math.sqrt(m));
      const answer = needed - 1;

      return numericExercise({
        prompt: `How many terms of this series guarantee an error of at most 1/${m}?`,
        latex: `\\sum_{n=1}^{\\infty} \\frac{(-1)^{n+1}}{${power === 1 ? 'n' : `n^{${power}}`}}`,
        value: answer,
        summary: `The error after n terms is at most the first term left out, which is 1 over (n+1)${power === 1 ? '' : ' squared'}. Asking for that to be no more than 1/${m} needs n + 1 ≥ ${needed}, so ${answer} terms are enough.`,
        hint: 'The bound names the first term you did *not* use. Solve for that term, then step back one.',
        steps: [
          { label: 'Write the bound.', latex: `|S - s_{n}| \\leq \\frac{1}{(n+1)${power === 1 ? '' : '^{2}'}}` },
          { label: 'Ask for it to be small enough.', latex: `\\frac{1}{(n+1)${power === 1 ? '' : '^{2}'}} \\leq \\frac{1}{${m}}` },
          { label: 'Rearrange.', latex: `n + 1 \\geq ${needed}` },
          { label: 'So the number of terms used is one less.', latex: `n = ${answer}` },
        ],
        traps: [trap(needed, 'alternating-error-bound-off-by-one')],
      });
    },
  }),

  template({
    id: 'alternating-absolute-or-conditional',
    conceptId: 'alternating-series',
    description: 'Tell absolute convergence from conditional.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = fraction(rng.int(1, 7), rng.pick([1, 2, 3]));
      const printed = p.d === 1 ? `${p.n}` : `${p.n}/${p.d}`;
      const absolute = p.n > p.d;

      return choiceExercise({
        prompt: 'How does this series converge?',
        latex: `\\sum_{n=1}^{\\infty} \\frac{(-1)^{n+1}}{${powerOfN(printed)}}`,
        ...options(
          [
            {
              label: 'Absolutely — the series of absolute values converges too',
              correct: absolute,
              ...(absolute ? {} : { misconceptionId: 'conditional-taken-as-absolute' }),
            },
            {
              label: 'Conditionally — it converges, but the series of absolute values does not',
              correct: !absolute,
              ...(absolute ? { misconceptionId: 'conditional-taken-as-absolute' } : {}),
            },
            { label: 'It does not converge at all', misconceptionId: 'alternating-decreasing-condition-skipped' },
            { label: 'Absolutely, because the alternating series test applies', misconceptionId: 'conditional-taken-as-absolute' },
          ],
          rng.shuffle,
        ),
        summary: absolute
          ? `Stripping the signs leaves the p-series with p = ${printed}, which converges because p is greater than 1. So the convergence does not depend on the cancellation, and is absolute.`
          : `Stripping the signs leaves the p-series with p = ${printed}, which diverges because p is not greater than 1. The alternating version still converges, by the alternating series test — so the convergence is conditional and depends entirely on the cancellation.`,
        hint: 'Take the absolute values first and test that series. Only if it diverges is the question about the alternating one.',
      });
    },
  }),

  template({
    id: 'alternating-conditions',
    conceptId: 'alternating-series',
    description: 'Check both conditions of the alternating series test.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const c = rng.int(2, 9);
      const p = rng.int(1, 4);

      return choiceExercise({
        prompt: 'The alternating series test needs terms that decrease and tend to zero. Which of these series satisfies both conditions?',
        ...options(
          [
            { latex: `\\sum_{n=1}^{\\infty} \\frac{(-1)^{n+1}}{n^{${p}} + ${c}}`, correct: true },
            { latex: `\\sum_{n=1}^{\\infty} (-1)^{n+1}\\frac{n}{n + ${c}}`, misconceptionId: 'alternating-decreasing-condition-skipped' },
            { latex: `\\sum_{n=1}^{\\infty} (-1)^{n+1}\\frac{n^{${p}}}{${c}}`, misconceptionId: 'alternating-decreasing-condition-skipped' },
            { latex: `\\sum_{n=1}^{\\infty} \\frac{1}{n^{${p}} + ${c}}`, misconceptionId: 'conditional-taken-as-absolute' },
          ],
          rng.shuffle,
        ),
        summary: `The first has terms decreasing to zero and alternating in sign. The second has terms tending to 1, the third has terms growing without bound, and the fourth does not alternate at all — so the test applies to none of them, whatever they may do for other reasons.`,
        hint: 'Two conditions, and a third thing that is easy to forget to check: that the series actually alternates.',
      });
    },
  }),

  /* ------------------------------------------------------- power series */
  template({
    id: 'radius-of-convergence',
    conceptId: 'power-series',
    description: 'Find the radius of convergence from the ratio test.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = rng.int(2, 9);
      const a = rng.int(-5, 5);
      const answer = fraction(1, c);
      const reciprocal = fraction(c, 1);

      return fractionExercise({
        prompt: 'Find the radius of convergence. Give your answer as a fraction.',
        latex: `\\sum_{n=1}^{\\infty} \\frac{${c}^{n}}{n}\\left(${shifted(a)}\\right)^{n}`,
        value: answer,
        summary: `The ratio of consecutive terms is ${c} times the size of ${minus(shifted(a))}, times n/(n+1), whose limit is 1. Convergence needs ${c}|${minus(shifted(a))}| < 1, so the radius is 1/${c}.`,
        hint: 'Take the ratio, factor the x out of the limit, then finish the inequality — the radius is what x is bounded by, not the limit itself.',
        steps: [
          { label: 'Form the ratio and cancel.', latex: `\\left|\\frac{a_{n+1}}{a_{n}}\\right| = ${c}\\left|${shifted(a)}\\right|\\cdot\\frac{n}{n+1}` },
          { label: 'The n-fraction tends to 1.', latex: `L = ${c}\\left|${shifted(a)}\\right|` },
          { label: 'Convergence needs L < 1.', latex: `\\left|${shifted(a)}\\right| < \\frac{1}{${c}}` },
          { label: 'Read off the radius.', latex: `R = \\frac{1}{${c}}` },
        ],
        traps: [fractionTrap(reciprocal, 'radius-taken-as-reciprocal')],
      });
    },
  }),

  template({
    id: 'interval-of-convergence',
    conceptId: 'power-series',
    description: 'Assemble the interval of convergence after testing both endpoints.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const a = rng.int(-4, 4);
      const b = rng.int(2, 6);
      const left = a - b;
      const right = a + b;

      return choiceExercise({
        prompt: `The ratio test gives a radius of ${b} for this series. What is its interval of convergence?`,
        latex: `\\sum_{n=1}^{\\infty} \\frac{\\left(${shifted(a)}\\right)^{n}}{n\\,${b}^{n}}`,
        ...options(
          [
            { latex: `[${left},\\ ${right})`, correct: true },
            { latex: `(${left},\\ ${right})`, misconceptionId: 'radius-endpoints-not-tested' },
            { latex: `[${left},\\ ${right}]`, misconceptionId: 'radius-endpoints-not-tested' },
            { latex: `(${left},\\ ${right}]`, misconceptionId: 'radius-endpoints-not-tested' },
          ],
          rng.shuffle,
        ),
        summary: `At x = ${right} the series becomes the sum of 1/n — the harmonic series, which diverges. At x = ${left} it becomes the alternating harmonic series, which converges. So the left end belongs to the interval and the right end does not.`,
        hint: 'Substitute each endpoint and test the number series you get. The two ends do not have to agree.',
      });
    },
  }),

  template({
    id: 'power-series-degenerate-radius',
    conceptId: 'power-series',
    description: 'Recognise the two degenerate radii, zero and infinite.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const kind = rng.pick(['zero', 'infinite', 'one'] as const);
      const k = rng.int(1, 4);
      const latex = {
        zero: '\\sum_{n=1}^{\\infty} n!\\,x^{n}',
        infinite: '\\sum_{n=1}^{\\infty} \\frac{x^{n}}{n!}',
        one: `\\sum_{n=1}^{\\infty} \\frac{x^{n}}{n^{${k}}}`,
      }[kind];

      return choiceExercise({
        prompt: 'What is the radius of convergence of this power series?',
        latex,
        ...options(
          [
            { label: 'Zero — it converges only at the centre', correct: kind === 'zero', ...(kind === 'zero' ? {} : { misconceptionId: 'radius-taken-as-reciprocal' }) },
            { label: 'Infinite — it converges for every x', correct: kind === 'infinite', ...(kind === 'infinite' ? {} : { misconceptionId: 'radius-taken-as-reciprocal' }) },
            { label: 'One', correct: kind === 'one', ...(kind === 'one' ? {} : { misconceptionId: 'radius-taken-as-reciprocal' }) },
            { label: 'It has no radius of convergence', misconceptionId: 'radius-endpoints-not-tested' },
          ],
          rng.shuffle,
        ),
        summary: {
          zero: 'The ratio is (n + 1)|x|, which runs off to infinity for every x except 0. A factorial in the numerator crushes the radius to nothing.',
          infinite: 'The ratio is |x|/(n + 1), which tends to 0 whatever x is. A factorial in the denominator makes the radius infinite — which is why the series for e to the x works everywhere.',
          one: `The ratio is |x| times (n/(n+1)) to the ${k}, whose limit is |x|. Convergence needs |x| < 1, so the radius is 1 — and the endpoints would then need testing separately.`,
        }[kind],
        hint: 'Look at where the factorial is. In the numerator it destroys the radius; in the denominator it makes it infinite.',
      });
    },
  }),

  /* ------------------------------------------------------ Taylor series */
  template({
    id: 'maclaurin-coefficient',
    conceptId: 'taylor-series',
    description: 'Find a Maclaurin coefficient of an exponential.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const c = rng.int(2, 4);
      const n = rng.int(2, 5);
      const factorial = [1, 1, 2, 6, 24, 120][n] as number;
      const answer = fraction(c ** n, factorial);
      const withoutFactorial = fraction(c ** n, 1);

      return fractionExercise({
        prompt: `What is the coefficient of x^${n} in the Maclaurin series of this function? Give your answer as a fraction.`,
        latex: `f(x) = e^{${c}x}`,
        value: answer,
        summary: `Each derivative brings down another ${c}, so the ${n}th derivative at 0 is ${c ** n}. The coefficient is that divided by ${n}! = ${factorial}, which is ${answer.n}/${answer.d}.`,
        hint: 'Differentiate n times, evaluate at 0, and then divide by n factorial — the division is not optional.',
        steps: [
          { label: 'Differentiate n times.', latex: `f^{(${n})}(x) = ${c}^{${n}}e^{${c}x}` },
          { label: 'Evaluate at the centre.', latex: `f^{(${n})}(0) = ${c ** n}` },
          { label: 'Divide by the factorial.', latex: `c_{${n}} = \\frac{${c ** n}}{${n}!} = \\frac{${answer.n}}{${answer.d}}` },
        ],
        traps: [fractionTrap(withoutFactorial, 'taylor-factorial-omitted')],
      });
    },
  }),

  template({
    id: 'maclaurin-shape',
    conceptId: 'taylor-series',
    description: 'Recognise a standard Maclaurin series by its shape.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const wantSine = rng.bool();
      const sineSeries = 'x - \\frac{x^{3}}{3!} + \\frac{x^{5}}{5!} - \\cdots';
      const cosineSeries = '1 - \\frac{x^{2}}{2!} + \\frac{x^{4}}{4!} - \\cdots';
      const sineNoFactorials = 'x - \\frac{x^{3}}{3} + \\frac{x^{5}}{5} - \\cdots';
      const cosineNoFactorials = '1 - \\frac{x^{2}}{2} + \\frac{x^{4}}{4} - \\cdots';
      const sineNoSigns = 'x + \\frac{x^{3}}{3!} + \\frac{x^{5}}{5!} + \\cdots';
      const cosineNoSigns = '1 + \\frac{x^{2}}{2!} + \\frac{x^{4}}{4!} + \\cdots';

      return choiceExercise({
        prompt: `Which is the Maclaurin series of ${wantSine ? 'sin x' : 'cos x'}?`,
        ...options(
          [
            { latex: wantSine ? sineSeries : cosineSeries, correct: true },
            { latex: wantSine ? cosineSeries : sineSeries, misconceptionId: 'taylor-derivative-not-evaluated' },
            { latex: wantSine ? sineNoFactorials : cosineNoFactorials, misconceptionId: 'taylor-factorial-omitted' },
            { latex: wantSine ? sineNoSigns : cosineNoSigns, misconceptionId: 'taylor-derivative-not-evaluated' },
          ],
          rng.shuffle,
        ),
        summary: `${wantSine ? 'Sine is odd, so only odd powers appear' : 'Cosine is even, so only even powers appear'} — the other coefficients vanish automatically, because ${wantSine ? 'every even derivative of sine' : 'every odd derivative of cosine'} is zero at the origin. The denominators are factorials, and the signs alternate because the derivatives cycle through plus, plus, minus, minus.`,
        hint: 'Check the parity of the powers first, then the denominators.',
      });
    },
  }),

  template({
    id: 'taylor-polynomial-value',
    conceptId: 'taylor-series',
    description: 'Approximate a value with the first three terms of a Maclaurin series.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const tenths = rng.int(1, 6);
      const h = tenths / 10;
      const answer = 1 + h + (h * h) / 2;
      const factorialOmitted = 1 + h + h * h;

      return numericExercise({
        prompt: `Estimate e^${h} using the first three terms of the Maclaurin series for e^x. Give your answer to three decimal places.`,
        value: answer,
        tolerance: within(0.001, answer),
        summary: `The series is 1 + x + x²/2 + …, so with x = ${h} the first three terms give ${answer.toFixed(3)}. The true value is ${Math.exp(h).toFixed(5)}, and the next term of the series accounts for almost all of the gap.`,
        hint: 'Three terms means up to and including the x² one — and that term has a 2! underneath it.',
        steps: [
          { label: 'Write the series.', latex: 'e^{x} = 1 + x + \\frac{x^{2}}{2!} + \\cdots' },
          { label: `Substitute x = ${h}.`, latex: `1 + ${h} + \\frac{${(h * h).toFixed(2)}}{2}` },
          { label: 'Add.', latex: `${answer.toFixed(3)}` },
        ],
        traps: [trap(factorialOmitted.toFixed(3), 'taylor-factorial-omitted')],
      });
    },
  }),
];
